import { useEffect } from "react";
import { Button, cn } from "@nextui-org/react";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BookingCalendar from "@/components/bookings/calendar/BookingCalendar";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { anyBookingCheckedIn, anyBookingLateOrMissed, getDayName } from "@/lib/helpers";
import { IBookingCalendar } from "@/lib/types";

// ---------------------- Props

interface BookingDayCalendarProps {
  day: number;
  dayOfMonth: number;
  date: Date;
  bookings: IBookingCalendar[];
}

// ---------------------- Component

const BookingDayCalendar = ({ day, dayOfMonth, date, bookings }: BookingDayCalendarProps) => {
  // --------- Variables / Hooks

  const { registerDate } = useBookingsCalendarStore();

  const { isDayExpanded, setExpandedDay } = useBookingsCalendarStore();

  const dateString = date.toDateString();

  const amIExpanded = isDayExpanded(dateString);

  const haveBookings = bookings.length > 0;

  const checkedIn = anyBookingCheckedIn(bookings);

  const hasLateOrMissed = anyBookingLateOrMissed(bookings);

  // --------- Effects

  useEffect(() => {
    registerDate(dateString);
  }, []);

  // --------- JSX

  return (
    <>
      <div
        className={cn(
          "flex h-auto w-full flex-col items-center justify-start rounded-lg border border-gray-200 transition-transform duration-500 ease-in-out dark:border-gray-800",
          [
            !amIExpanded && "opacity-80",
            amIExpanded &&
              "my-1 border-gray-300 bg-gray-100/20 dark:border-gray-700 dark:bg-gray-800/10",
          ],
        )}
      >
        <div className="w-full">
          <div
            className="flex h-auto w-full flex-row items-center justify-between px-3 py-1"
            onClick={() => setExpandedDay(dateString)}
          >
            {/*******************************************/}
            {/* Date Info */}
            {/*******************************************/}

            <div className="flex h-auto w-full flex-row items-center justify-start xs:gap-3 sm:gap-5">
              <p className="w-7 text-sm font-semibold text-gray-600 dark:text-gray-300 sm:text-base">
                {dayOfMonth}
              </p>
              <p className="text-base font-medium text-gray-800 dark:text-gray-200 sm:text-lg">
                {getDayName(day)}
              </p>
            </div>

            <div className="-mr-3 flex h-auto w-full flex-row items-center justify-end gap-1">
              {/*******************************************/}
              {/* Issues Icon */}
              {/*******************************************/}

              {hasLateOrMissed && (
                <Button
                  isIconOnly
                  radius="full"
                  className="bg-transparent p-2 text-current transition-all duration-200"
                >
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    className="h-4 text-[#ff321c] transition-transform duration-500"
                  />
                </Button>
              )}

              {/*******************************************/}
              {/* Booking Count Indicator */}
              {/*******************************************/}

              <div
                className={cn(
                  "relative flex h-6 w-6 flex-col items-center justify-center rounded-full bg-gray-800/70 pl-[0.025rem] pt-[0.1rem] text-xs font-bold text-white",
                  !haveBookings && "bg-gray-800/70",
                  checkedIn && "bg-[#0cb307]",
                )}
              >
                {checkedIn ? (
                  <>
                    <span className="absolute -mt-[0.15rem] inline-flex h-6 w-6 animate-ping rounded-full bg-[#0cb307] opacity-65"></span>
                    <span className="relative">{bookings.length}</span>
                  </>
                ) : (
                  <span>{bookings.length}</span>
                )}
              </div>

              {/*******************************************/}
              {/* Show/Hide Button */}
              {/*******************************************/}

              <Button
                isIconOnly
                radius="full"
                className="bg-transparent p-2 text-current transition-all duration-200"
                onPress={() => setExpandedDay(dateString)}
              >
                <FontAwesomeIcon
                  icon={amIExpanded ? faChevronUp : faChevronDown}
                  className="h-3 text-gray-500 transition-transform duration-500 xs:h-4"
                />
              </Button>
            </div>
          </div>

          {/*******************************************/}
          {/* Bookings List */}
          {/*******************************************/}

          <div
            className={cn(
              "overflow-hidden transition-all duration-500",
              amIExpanded ? "opacity-100" : "h-0 opacity-0",
            )}
          >
            <div className="">
              <div
                className={cn(
                  "mx-2 flex h-auto w-auto flex-col items-start justify-start gap-2 rounded-lg bg-white/20 py-3 pt-1 transition-all duration-500 ease-in-out dark:bg-black/20",
                  [haveBookings && "my-2 border border-gray-200 dark:border-gray-800"],
                )}
              >
                {haveBookings ? (
                  bookings.map((booking, i, arr) => (
                    <div
                      key={booking.bookingId}
                      className="w-full"
                    >
                      <BookingCalendar
                        date={dateString}
                        booking={booking}
                        last={i === arr.length - 1}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-800 dark:text-gray-200 sm:text-base">
                    No bookings today.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingDayCalendar;
